import React from 'react';
import { BookOpen, Clock, Heart } from 'lucide-react';
import { Button } from '../ui/button';
import { demoFeedData } from '../../utils/demoFeedData';

// Define StoryItem type to match demo data structure
export interface StoryItem {
  id: string;
  title: string;
  author: string;
  cover_image?: string;
  excerpt?: string;
  category?: string;
  read_time?: string;
  likes?: number;
  published_at?: string;
}

interface StoriesCarouselProps {
  stories?: StoryItem[];
  title?: string;
}

const StoriesCarousel: React.FC<StoriesCarouselProps> = ({ 
  stories = demoFeedData.sampleStoryItems, 
  title = "Latest Stories" 
}) => {
  if (!stories || stories.length === 0) {
    return null;
  }

  return (
    <div className="w-full bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#065900]" />
          {title}
        </h3>
        <Button variant="ghost" size="sm" className="text-[#065900] hover:bg-[#065900]/10">
          View All
        </Button>
      </div>
      
      <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
        {stories.map((story) => (
          <div
            key={story.id}
            className="flex-shrink-0 w-64 bg-gray-50 rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer group"
          >
            {story.cover_image && (
              <div className="w-full h-32 bg-gray-200 overflow-hidden">
                <img
                  src={story.cover_image}
                  alt={story.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            )}
            
            <div className="p-3">
              <h4 className="font-medium text-gray-900 text-sm line-clamp-2 mb-2">
                {story.title}
              </h4>
              
              <p className="text-xs text-gray-600 mb-2">
                by {story.author}
              </p>
              
              {story.excerpt && (
                <p className="text-xs text-gray-500 line-clamp-2 mb-3">
                  {story.excerpt}
                </p>
              )}
              
              <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center gap-3">
                  {story.read_time && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {story.read_time}
                    </span>
                  )}
                  {story.likes && (
                    <span className="flex items-center gap-1">
                      <Heart className="w-3 h-3" />
                      {story.likes}
                    </span>
                  )}
                </div>
                {story.category && (
                  <span className="bg-[#065900]/10 text-[#065900] px-2 py-1 rounded text-xs">
                    {story.category}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StoriesCarousel;
